import { eyeTestData } from './test-data';
import { StorageService } from '../../shared/logic/storage-service';
import { GlobalLogic } from '../../shared/logic/global-logic';

export interface ILandoltContainer {
    id: number;             // from 1 to 24 where 1 is the biggest and 24 is the smallest landolt ring
    baseSize: number;       // in pt
    visusOneMeter: number;  // Visus for 1m distance
    direction: ILandoltDirection;
    seen: number;
    correctGuess: number;
    wrongGuess: number;
    identified: boolean;    // if testing person identifies two out of three rings from one size, set this to true
    identifiedCorrectly: boolean;
    identifiedWrongly: boolean;
    color: string;
}

export enum IDirection {
    LL = 'LL',
    LC = 'LC',
    LR = 'LR',
    ML = 'ML',
    MR = 'MR',
    TL = 'TL',
    TC = 'TC',
    TR = 'TR',
    DONT_KNOW = 'DONT_KNOW',
    UNKNOWN = 'UNKNOWN'
}

export interface ILandoltDirection {
    code: number;           // font-code from 1 to 8
    direction: IDirection;
}

export enum NextLandoltIs {
    SMALLER, BIGGER
}

export abstract class TestLogic {

    protected readonly _storageService: StorageService;
    protected readonly _globalLogic: GlobalLogic;

    protected readonly _landolts: ILandoltContainer[];
    protected readonly _max: number;
    private _finished: boolean;

    protected constructor() {
        this._storageService = StorageService.getInstance();
        this._globalLogic = GlobalLogic.getInstance();
        this._landolts = [];
        this._max = eyeTestData.length;

        for (let i = 1; i <= eyeTestData.length; ++i) {
            const code: number = TestLogic._getRandomLandoltId();
            this._landolts.push({
                id: i,
                baseSize: eyeTestData[i - 1].baseSize,
                visusOneMeter: eyeTestData[i - 1].baseVisus,
                direction: {
                    code,
                    direction: TestLogic._getDirectionByLandoltCode(code)
                } as ILandoltDirection,
                seen: 0,
                correctGuess: 0,
                wrongGuess: 0,
                identified: false,
                identifiedCorrectly: false,
                identifiedWrongly: false,
                color: '#000000'
            } as ILandoltContainer);
        }
        this._finished = false;
    }

    static getDirectionByKeypadNum(keypadNum: number): IDirection {
        switch (keypadNum) {
            case 1:
                return IDirection.LL;
            case 2:
                return IDirection.LC;
            case 3:
                return IDirection.LR;
            case 4:
                return IDirection.ML;
            case 5:
                return IDirection.DONT_KNOW;
            case 6:
                return IDirection.MR;
            case 7:
                return IDirection.TL;
            case 8:
                return IDirection.TC;
            case 9:
                return IDirection.TR;
            default:
                return IDirection.UNKNOWN;
        }
    }

    protected static _getDifferentDirection(lastDirection: ILandoltDirection): ILandoltDirection {
        while (true) {
            const newDirection: number = TestLogic._getRandomLandoltId();
            if (lastDirection.code !== newDirection) {
                return {
                    code: newDirection,
                    direction: TestLogic._getDirectionByLandoltCode(newDirection)
                } as ILandoltDirection;
            }
        }
    }

    protected static _getRandomLandoltId(): number {
        const buf = new Uint8Array(1);
        window.crypto.getRandomValues(buf);
        return buf[0] % (9 - 1) + 1;
    }

    protected static _getDirectionByLandoltCode(landoltCode: number): IDirection {
        switch (landoltCode) {
            case 1:
                return IDirection.MR;
            case 2:
                return IDirection.TC;
            case 3:
                return IDirection.ML;
            case 4:
                return IDirection.LC;
            case 5:
                return IDirection.TL;
            case 6:
                return IDirection.LL;
            case 7:
                return IDirection.LR;
            case 8:
                return IDirection.TR;
            default:
                return IDirection.UNKNOWN;
        }
    }

    get finished(): boolean {
        return this._finished;
    }

    abstract type(): string;

    abstract current(): ILandoltContainer;

    /**
     * Applies user input.
     * @return true if the test should continue
     */
    abstract apply(direction: IDirection): boolean;

    abstract resetState(): void;

    abstract get smallestCorrectLandolt(): ILandoltContainer;

    abstract get smallestCorrectLandoltReducedOpacity(): ILandoltContainer;

    abstract getTelemetryData(): any | null;

    get landolts(): ILandoltContainer[] {
        return this._landolts;
    }

    protected _markTestAsFinished(): void {
        this._finished = true;
    }

    protected _resetLandolts(): void {
        this._landolts.forEach(l => {
            l.seen = 0;
            l.correctGuess = 0;
            l.wrongGuess = 0;
            l.identified = false;
            l.identifiedCorrectly = false;
            l.identifiedWrongly = false;
            l.color = '#000000';
        });
    }
}
